import { Component, ElementRef, HostListener, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, afterNextRender, computed, inject, input, output, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { Router } from '@angular/router'
import { AsiakkaanTuote, LaskuTuotteetHakuPyynto, LaskuTuotteetHakuPyyntoLemonator, LaskuTuotteetHakuVastaus } from '@lemon/core-firebase/model/lasku'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskutusHakuStateService } from 'app/_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { EMPTY, Subject, firstValueFrom, from } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'app-tuoterekisteri-haku-lemonator',
  templateUrl: './tuoterekisteri-haku-lemonator.component.html',
  styleUrls: ['./tuoterekisteri-haku-lemonator.component.css'],
  standalone: false,
})
export class TuoterekisteriHakuLemonatorComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      afterNextRender(() => kentta.nativeElement.focus(), { injector: this._injector })
    }
  }

  readonly hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly tulokset = signal<AsiakkaanTuote[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly sivu = signal(0)
  readonly sivunKoko = signal(50)
  readonly sivukoot = [50, 100, 200]

  private readonly _injector = inject(Injector)
  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _hakuStateService: LaskutusHakuStateService,
    private _router: Router,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          const hakuehdotPuuttuvat = !this.hakuteksti().value().trim()
          if (hakuehdotPuuttuvat) {
            this.haetaan.set(false)
            this.tulokset.set([])
            this.kokonaismaara.set(0)
            this._edellinenHakuJson = null
            return EMPTY
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))

    const tallennettuHakutila = this._hakuStateService.tuote.onkoHakutilaTallennettu(this._router.url) ? this._hakuStateService.tuote.palautaHakutila() : null
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.sivu.set(tallennettuHakutila.sivu)
      this.sivunKoko.set(tallennettuHakutila.sivunKoko)
      this._hakuTrigger.next()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (this.avoinna()) {
      this.sulje()
    }
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this.sivu.set(0)
    this._hakuTrigger.next()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu.set(event.pageIndex)
    this.sivunKoko.set(event.pageSize)
    this._hakuTrigger.next()
  }

  async avaaTuote(tuote: AsiakkaanTuote) {
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!avainTiedot?.avain) {
      return
    }
    this._hakuStateService.tuote.tallennaHakutila({ hakuteksti: this.hakuteksti().value(), sivu: this.sivu(), sivunKoko: this.sivunKoko(), paluureitti: this._router.url })
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'tuotteet', 'selaa', tuote.$key])
  }

  private async _suoritaHaku(pyynto: LaskuTuotteetHakuPyynto): Promise<LaskuTuotteetHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
      const lemonatorPyynto: LaskuTuotteetHakuPyyntoLemonator = { ...pyynto, asiakasAvain: avainTiedot.avain }
      return await this._firebaseLemonaid.functionsCall<LaskuTuotteetHakuPyyntoLemonator, LaskuTuotteetHakuVastaus>('laskutEtsiTuoteLemonator', lemonatorPyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: LaskuTuotteetHakuVastaus | null, pyyntoJson: string) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set(vastaus.tuotteet ?? [])
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
  }

  private _rakennaHakupyynto(): LaskuTuotteetHakuPyynto {
    const pyynto: LaskuTuotteetHakuPyynto = { ylaraja: this.sivunKoko(), sivu: this.sivu() }
    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }
    return pyynto
  }
}
